import { useTheme } from "@mui/material";
import { useUserManagementServiceV1 } from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { FC, useCallback, useDeferredValue, useMemo, useState } from "react";
import {
  ConfirmationDialog,
  SearchBar,
  SelectableList,
} from "../../../components";
import { ALL_RELEASE_CHANNELS_QUERY_KEY } from "../../../hooks/useAllExperiences";
import { USER_GROUPS_QUERY_KEY } from "../../../hooks/useAllUserGroupsForACompany";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useLocalization } from "../../../hooks/useLocalization";
import { INCOMING_LICENSES_QUERY_KEY } from "../../experiences/hooks/useIncomingLicenses";
import { USERS_FOR_RELEASE_CHANNEL_QUERY_KEY } from "../../experiences/hooks/useUsersForReleaseChannel";
import { USER_GROUP_QUERY_KEY } from "../../groups/hooks/useUserGroupById";
import { USERS_IN_GROUP_QUERY_KEY } from "../../groups/hooks/useUsersInGroup";
import { ALL_LICENSES_QUERY_KEY } from "../../licenses/hooks/useExternalLicenses";
import { ErrorDisplay } from "../../users/AddNewUser/ErrorDisplay";
import { USER_RELEASE_CHANNELS_QUERY_KEY } from "../../users/hooks/useUserExperiences";
import { useUsers } from "../../users/hooks/useUsers";
import { UserRow } from "./UsersTab";

type UserCacheData =
  | InfiniteData<
      | UserManagement_V1.UserWithCompanyName[]
      | { users: UserManagement_V1.UserWithCompanyName[] }
    >
  | UserManagement_V1.UserWithCompanyName[];

export interface AddUserToGroupDialogProps {
  isOpen: boolean;
  onClose: () => void;
  companyId?: string;
  groupId?: string;
  usersToExclude?: UserRow[];
}

export const AddUserToGroupDialog: FC<AddUserToGroupDialogProps> = ({
  isOpen,
  onClose,
  companyId,
  groupId,
  usersToExclude,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const userManagementService = useUserManagementServiceV1();

  const isMobile = useIsMobile();

  const { users: allUsers, isLoading } = useUsers({
    companyId,
  });

  // --- Helper for Optimistic Updates ---
  const updateCacheOptimistically = (userIds: string[]) => {
    if (!groupId) return;

    const usersToAdd = allUsers.filter((u) => userIds.includes(u.userId));

    queryClient.setQueriesData<UserCacheData>(
      { queryKey: [USERS_IN_GROUP_QUERY_KEY, groupId] },
      (oldData) => {
        if (!oldData) return oldData;

        if ("pages" in oldData) {
          const currentPages = oldData.pages;
          if (currentPages.length === 0) return oldData;

          const firstPage = currentPages[0];

          if (Array.isArray(firstPage)) {
            const updatedFirstPage = [
              ...usersToAdd,
              ...firstPage,
            ] as UserManagement_V1.UserWithCompanyName[];

            return {
              ...oldData,
              pages: [updatedFirstPage, ...currentPages.slice(1)],
            };
          }

          if (
            firstPage &&
            typeof firstPage === "object" &&
            "users" in firstPage
          ) {
            const updatedFirstPage = {
              ...firstPage,
              users: [...usersToAdd, ...firstPage.users],
            };

            return {
              ...oldData,
              pages: [updatedFirstPage, ...currentPages.slice(1)],
            };
          }

          return oldData;
        }

        if (Array.isArray(oldData)) {
          return [...usersToAdd, ...oldData];
        }

        return oldData;
      },
    );
  };

  const addUsersToGroupMutation = useMutation({
    mutationFn: async (userIds: string[]) => {
      if (!groupId) {
        throw new Error("Group ID is required");
      }

      try {
        return await userManagementService.addManyUsersToUserGroup(
          groupId,
          userIds,
        );
      } catch (error) {
        if (
          error &&
          typeof error === "object" &&
          "status" in error &&
          error.status === 204 &&
          "response" in error &&
          error.response === ""
        ) {
          return error.response;
        }
        throw error;
      }
    },
    onSuccess: (_, userIds) => {
      // OPTIMISTIC UPDATE
      updateCacheOptimistically(userIds);

      // Helper for invalidation
      const invalidateQueries = () => {
        void queryClient.invalidateQueries({
          queryKey: [USERS_IN_GROUP_QUERY_KEY, groupId],
        });
        void queryClient.invalidateQueries({
          queryKey: [USER_GROUP_QUERY_KEY, groupId],
        });
        void queryClient.invalidateQueries({
          queryKey: [USER_RELEASE_CHANNELS_QUERY_KEY],
        });
        void queryClient.invalidateQueries({
          queryKey: [INCOMING_LICENSES_QUERY_KEY],
        });
        void queryClient.invalidateQueries({
          queryKey: [ALL_LICENSES_QUERY_KEY],
        });
        void queryClient.invalidateQueries({
          queryKey: [ALL_RELEASE_CHANNELS_QUERY_KEY],
        });
        void queryClient.invalidateQueries({
          queryKey: [USERS_FOR_RELEASE_CHANNEL_QUERY_KEY],
        });
        for (const userId of userIds) {
          void queryClient.invalidateQueries({
            queryKey: [USER_RELEASE_CHANNELS_QUERY_KEY, userId],
          });
          void queryClient.invalidateQueries({
            queryKey: [USER_GROUPS_QUERY_KEY, userId],
          });
        }
      };

      // Double Invalidation
      invalidateQueries();
      setTimeout(invalidateQueries, 1000);

      onClose();
      setSelectedUserIds([]);
      setSearchTerm("");
    },
  });

  const mappedUsers = useMemo(() => {
    return allUsers.map((user) => ({
      id: user.userId,
      primaryText: `${user.surname}, ${user.forename}`,
      secondaryText: user.emailAddress,
    }));
  }, [allUsers]);

  const filteredUsers = useMemo(() => {
    let users = mappedUsers.filter(
      (user) =>
        !usersToExclude?.some(
          (excludedUser) => excludedUser.userId === user.id,
        ),
    );

    if (deferredSearchTerm.trim()) {
      users = users.filter((user) =>
        `${user.primaryText.split(", ").reverse().join(" ")} ${user.secondaryText}`
          .toLowerCase()
          .includes(deferredSearchTerm.toLowerCase()),
      );
    }

    return users;
  }, [deferredSearchTerm, mappedUsers, usersToExclude]);

  const handleUserSelect = useCallback((userIdToSelect: string) => {
    setSelectedUserIds((prev) => {
      if (prev.includes(userIdToSelect)) {
        return prev.filter((id) => id !== userIdToSelect);
      } else {
        return [...prev, userIdToSelect];
      }
    });
  }, []);

  const handleRemoveUser = useCallback((userIdToRemove: string) => {
    setSelectedUserIds((prev) => prev.filter((id) => id !== userIdToRemove));
  }, []);

  const handleSubmit = () => {
    if (selectedUserIds.length > 0) {
      addUsersToGroupMutation.mutate(selectedUserIds);
    }
  };

  const handleClear = useCallback(() => {
    setSearchTerm("");
  }, []);

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
  };

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    [],
  );

  const handleCancel = () => {
    onClose();
    setSelectedUserIds([]);
    setSearchTerm("");
    addUsersToGroupMutation.reset();
  };

  const addButtonText = useMemo(() => {
    if (addUsersToGroupMutation.isPending) {
      return t("groups.addUserToGroupDialog.adding");
    }
    if (isMobile) {
      return t("groups.addUserToGroupDialog.add");
    }
    if (selectedUserIds.length === 0) {
      return t("groups.addUserToGroupDialog.addUserToGroup");
    }
    if (selectedUserIds.length === 1) {
      return t("groups.addUserToGroupDialog.addOneUser");
    }

    return t("groups.addUserToGroupDialog.addMultipleUsers", {
      count: selectedUserIds.length,
    });
  }, [addUsersToGroupMutation.isPending, isMobile, selectedUserIds.length, t]);

  const selectedItems = useMemo(() => {
    return mappedUsers.filter((item) => selectedUserIds.includes(item.id));
  }, [mappedUsers, selectedUserIds]);

  return (
    <ConfirmationDialog.Root
      aria-describedby="add-user-to-group-description"
      aria-labelledby="add-user-to-group-title"
      fullScreenOnMobile={true}
      fullWidth
      height="728px"
      maxWidth="xs"
      onClose={handleCancel}
      open={isOpen}
    >
      <ConfirmationDialog.Title id="add-user-to-group-title" showIcon={false}>
        {t("groups.addUserToGroupDialog.title")}
      </ConfirmationDialog.Title>
      <ConfirmationDialog.Content
        sx={{
          fontWeight: 500,
          color: theme.colors.text[1],
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <form onSubmit={handleSearchSubmit} style={{ width: "100%" }}>
          <SearchBar
            handleClear={handleClear}
            handleSubmit={handleSearchSubmit}
            onChange={handleSearchChange}
            placeholder={t("groups.addUserToGroupDialog.searchPlaceholder")}
            sx={{ maxWidth: undefined }}
            value={searchTerm}
          />
        </form>

        <SelectableList
          emptyMessage={t("groups.addUserToGroupDialog.noUsersAvailable")}
          height="100%"
          isLoading={isLoading}
          items={filteredUsers}
          maxHeight="100%"
          minHeight={0}
          multiSelect={true}
          onChipRemove={handleRemoveUser}
          onItemSelect={handleUserSelect}
          selectedIds={selectedUserIds}
          selectedItems={selectedItems}
          selectedItemsTitle={t("groups.addUserToGroupDialog.selectedUsers")}
          sx={{ flex: 1, minHeight: 0 }}
        />
        {addUsersToGroupMutation.error && (
          <ErrorDisplay error={addUsersToGroupMutation.error} />
        )}
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions>
        <ConfirmationDialog.Button
          onClick={handleCancel}
          sx={{
            color: theme.colors.primary[5],
          }}
          variant="secondary"
        >
          {t("groups.addUserToGroupDialog.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          disabled={
            selectedUserIds.length === 0 || addUsersToGroupMutation.isPending
          }
          onClick={handleSubmit}
          variant="contained"
        >
          {addButtonText}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </ConfirmationDialog.Root>
  );
};
